/** @prettier */
import ClientRedirect from '@xanadu/ui-shared/components/ClientRedirect';
import DepositWithdrawalCheckContent from '@xanadu/ui-shared/components/DepositWithdrawalCheck';
import KycMessaging from '@xanadu/ui-shared/components/KycMessaging';
import GraiDualVerification from '@xanadu/ui-shared/components/KycMessaging/components/GraiDualVerification';
import ModalSubComponents from '@xanadu/ui-shared/components/ModalSubComponents';
import MarketRules from '@xanadu/ui-shared/components/Modals/MarketRulesModal';
import MarketingPreferencesModal from '@xanadu/ui-shared/components/Modals/MarketingPreferencesModal';
import SwitcherModal from '@xanadu/ui-shared/components/Modals/SwitcherModal';
import ResetPassword from '@xanadu/ui-shared/components/ResetPassword';
import RgInteraction from '@xanadu/ui-shared/components/ResponsibleGambling/RgInteraction';
import ReviewAccountActivityModal from '@xanadu/ui-shared/components/ReviewAccountActivity';
import SignUp from '@xanadu/ui-shared/components/SignUp';
import TermsAndConditionsModal from '@xanadu/ui-shared/components/TermsAndConditionsModal';
import UploadDocuments from '@xanadu/ui-shared/components/UploadDocuments/UploadDocuments';
import UploadDocumentsSuccess from '@xanadu/ui-shared/components/UploadDocuments/UploadDocumentsSuccess';
import async from '@xanadu/ui-shared/components/asyncComponent';
import NetDepositLimitModalContent from '@xanadu/ui-shared/components/netDepositLimit';

import { MOBILE_BREAKPOINT } from '../../constants';

import FreeBetsPopupWrapper from '../FreeBetsPopupWrapper';
import Login from '../Login';
import PreviousLoggedIn from '../PreviousLoggedIn';
import SignUpHead from '../SignUpHead';

const AsyncCashier = async(() =>
  import('@xanadu/ui-shared/components/Cashier').then((module) => module.default)
);

const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

const contents = ({ SignUp: SignUpConfig = {}, kyc = {} } = {}) => [
  // each content can have a dedicated layout
  // chosen between 'fullscreen', 'large', 'medium' and 'small', default is on 'medium'
  {
    section: 'login',
    component: Login,
    layout: 'auto',
    showCloseIcon: !isMobile,
  },
  {
    section: 'registration',
    component: SignUp,
    componentProps: {
      Head: SignUpHead,
      hideLogo: true,
      RegLogos: SignUpConfig.RegLogos,
      showPoweredBy: SignUpConfig.showPoweredBy,
      trustPilotWidgetEnable: SignUpConfig.trustPilotWidgetEnable,
      spaced: true,
      childrenProps: {
        RegLogos: {
          logosPosition: 'bottom',
          centered: true,
          ...(SignUpConfig.childrenProps && SignUpConfig.childrenProps.RegLogos
            ? SignUpConfig.childrenProps.RegLogos
            : {}),
        },
        SignUpForm: {
          childrenProps: {
            SignUpFormStep3: {
              dividers: {
                1: {
                  margin: '107px 0 24px 0',
                },
              },
            },
          },
          ...(SignUpConfig.childrenProps && SignUpConfig.childrenProps.SignUpForm
            ? SignUpConfig.childrenProps.SignUpForm
            : {}),
        },
        SignUp: { ...SignUpConfig },
      },
    },
    showCloseIcon: false,
    layout: 'fullscreen',
  },
  {
    section: 'previous-logged-in',
    component: PreviousLoggedIn,
  },
  {
    section: 'update-password',
    component: ResetPassword,
    showCloseIcon: false,
    layout: 'fullscreen',
  },
  {
    section: 'deposit',
    component: AsyncCashier,
    layout: 'auto',
    size: {
      width: 640,
      height: 620,
    },
    componentProps: {
      key: 'mb-modal-deposit', // TODO: is this used?
      section: 'deposit',
    },
  },
  {
    section: 'withdraw',
    component: AsyncCashier,
    layout: 'custom',
    size: {
      width: 640,
      height: 600,
    },
    componentProps: {
      key: 'mb-modal-withdraw', // TODO: is this used?
      section: 'withdraw',
    },
  },
  {
    section: 'new-free-bets',
    component: FreeBetsPopupWrapper,
  },
  {
    section: 'review-account-activity',
    component: ReviewAccountActivityModal,
    showCloseIcon: true,
    layout: 'auto',
  },
  {
    section: 'rg-interaction',
    component: RgInteraction,
    showCloseIcon: false,
    layout: 'custom',
    size: {
      width: 480,
      height: 'auto',
    },
  },
  {
    section: 'marketing-preferences',
    component: MarketingPreferencesModal,
    layout: 'auto',
  },
  {
    section: 'market-rules',
    component: MarketRules,
    showCloseIcon: true,
  },
  {
    section: 'switcher',
    component: SwitcherModal,
    showCloseIcon: true,
    layout: 'auto',
  },
  {
    section: 'kyc-messaging',
    component: KycMessaging,
    layout: 'auto',
    componentProps: kyc,
  },
  {
    section: 'deposit-withdrawal-check',
    component: DepositWithdrawalCheckContent,
    layout: 'small',
  },
  {
    section: 'kyc-documents-warning',
    component: KycMessaging,
    layout: 'large',
    componentProps: {
      signUpSuccess: true,
    },
  },
  {
    section: 'upload-documents',
    component: UploadDocuments,
    layout: 'custom',
    size: {
      minWidth: 524,
      minHeight: 520,
    },
  },
  {
    section: 'grai-verification',
    component: GraiDualVerification,
    layout: 'custom',
    size: {
      minWidth: 524,
      minHeight: 420,
    },
  },
  {
    section: 'upload-success',
    component: UploadDocumentsSuccess,
  },
  {
    section: 'terms-and-conditions',
    component: TermsAndConditionsModal,
    showCloseIcon: false,
    componentProps: {
      key: 'mb-modal-terms-and-conditions',
    },
  },
  {
    section: 'deposit-allowance',
    component: NetDepositLimitModalContent,
    layout: 'small',
  },
  {
    section: 'session-timeout',
    component: ModalSubComponents.SessionTimeout,
    layout: 'small',
  },
  {
    section: 'banned-country',
    component: ModalSubComponents.BannedCountry,
    showCloseIcon: false,
  },
  {
    section: 'events-not-available',
    component: ModalSubComponents.EventsNotAvailable,
    layout: 'small',
  },
  {
    section: 'client-redirect',
    component: ClientRedirect,
    layout: isMobile ? 'small' : 'medium',
  },
];

export default contents;
